import { useEffect, useMemo, useState } from "react";
import { NICHES } from "./PlanProposal";

const API = process.env.REACT_APP_BACKEND_URL;

// Default payment links — kept identical to the ones baked into /plan
// Payment checkout URLs are now hardcoded in PlanProposal (CHECKOUT_URL / CHECKOUT_SPLIT_URL).
// PlanBuilder no longer accepts custom URLs — reps only toggle which buttons show.

const money = (n) => "$" + Math.round(Number(n) || 0).toLocaleString("en-US");

const formatWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export default function PlanBuilder({ user, onLogout, embedded = false }) {
  // Team-wide custom niches — synced from the backend so any niche a rep adds
  // (e.g. "landscaping") appears in every teammate's dropdown.
  const [customNiches, setCustomNiches] = useState({}); // { slug: {name, suffix, perShown, ...} }
  const [customNichesLoading, setCustomNichesLoading] = useState(false);

  const loadCustomNiches = async () => {
    setCustomNichesLoading(true);
    try {
      const r = await fetch(`${API}/api/sales/niches`, { credentials: "include" });
      if (!r.ok) throw new Error("fetch failed");
      const data = await r.json();
      const map = {};
      (data.niches || []).forEach((n) => {
        map[n.slug] = {
          name: n.name,
          suffix: n.suffix,
          setup: 5000,
          perShown: n.per_shown,
          defaultJob: 1200,
          defaultClose: 50,
          custom: true,
          createdBy: n.created_by_name || "",
        };
      });
      setCustomNiches(map);
    } catch (e) { /* silent — dropdown just won't show custom entries */ }
    setCustomNichesLoading(false);
  };

  useEffect(() => { loadCustomNiches(); }, []);

  // Merged niche map used throughout the builder — built-in wins if slug collides
  const allNiches = useMemo(() => ({ ...customNiches, ...NICHES }), [customNiches]);

  const [nicheKey, setNicheKey] = useState("general");
  const niche = allNiches[nicheKey] || NICHES.general;

  // Add-niche modal state — pricing is a flat $79 for every niche
  const [showAddNiche, setShowAddNiche] = useState(false);
  const [newNicheSlug, setNewNicheSlug] = useState("");
  const [newNicheName, setNewNicheName] = useState("");
  const [nicheErr, setNicheErr] = useState("");
  const [nicheBusy, setNicheBusy] = useState(false);

  const saveNewNiche = async () => {
    const slug = newNicheSlug.trim().toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
    const name = newNicheName.trim();
    if (!slug) { setNicheErr("Slug required (e.g. landscaping)"); return; }
    if (!name) { setNicheErr("Display name required"); return; }
    if (NICHES[slug]) { setNicheErr(`"${slug}" is a built-in niche`); return; }
    setNicheBusy(true);
    setNicheErr("");
    try {
      const r = await fetch(`${API}/api/sales/niches`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug, name, per_shown: 79, suffix: `For ${name} Companies` }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ detail: "Save failed" }));
        setNicheErr(err.detail || "Save failed");
        setNicheBusy(false);
        return;
      }
      await loadCustomNiches();
      setNicheKey(slug);
      setPerShown(79);
      setShowAddNiche(false);
      setNewNicheSlug("");
      setNewNicheName("");
    } catch (e) {
      setNicheErr("Network error — please try again");
    }
    setNicheBusy(false);
  };

  const removeCustomNiche = async (slug) => {
    if (!customNiches[slug]) return;
    if (!window.confirm(`Remove "${customNiches[slug].name}" for the whole team?`)) return;
    try {
      await fetch(`${API}/api/sales/niches/${encodeURIComponent(slug)}`, { method: "DELETE", credentials: "include" });
      await loadCustomNiches();
      if (nicheKey === slug) setNicheKey("general");
    } catch (e) { /* ignore */ }
  };

  const [company, setCompany] = useState("");
  const [first, setFirst] = useState("");
  const [job, setJob] = useState("");
  const [close, setClose] = useState("");
  const [appts, setAppts] = useState("");
  const setupFee = 5000; // Hardcoded — matches the fixed checkout URL
  const [perShown, setPerShown] = useState(79);
  const [exp, setExp] = useState("");
  const [loom, setLoom] = useState("");
  const [repName, setRepName] = useState("");
  const [repPhoto, setRepPhoto] = useState("");
  const [repEmail, setRepEmail] = useState("");

  // Payment button visibility — URLs are hardcoded, rep only controls which buttons show
  const [showFull, setShowFull] = useState(true);
  const [showSplit, setShowSplit] = useState(true);
  const [rescheduleUrl, setRescheduleUrl] = useState("");

  // Stable client-side plan_id so /plan can track analytics against it.
  // Persists across the form edits until Save (then baked into the row).
  const [planId, setPlanId] = useState(() =>
    (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `pid_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
  );

  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [meId, setMeId] = useState(null);

  // Live preview panel state — device switcher + collapse toggle
  const [previewOpen, setPreviewOpen] = useState(true);
  const [previewDevice, setPreviewDevice] = useState("desktop"); // "desktop" | "mobile"
  // Debounce the URL fed to the iframe so rapid typing doesn't hammer reloads
  const [previewSrc, setPreviewSrc] = useState("");
  // "mine" | "team" — persisted so each rep's choice sticks
  const [scope, setScope] = useState(() => {
    try { return localStorage.getItem("planbuilder.scope") || "mine"; }
    catch (e) { return "mine"; }
  });

  // ─── Saved templates (localStorage, per-browser/per-rep) ───
  // Reusable niche+pricing+loom config combos so reps can spin up plans in seconds.
  const TPL_KEY = "planbuilder.templates.v1";
  const [templates, setTemplates] = useState(() => {
    try {
      const raw = localStorage.getItem(TPL_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (e) { return []; }
  });
  const [tplName, setTplName] = useState("");
  const [tplPromptOpen, setTplPromptOpen] = useState(false);
  const [tplMsg, setTplMsg] = useState("");

  const persistTemplates = (arr) => {
    setTemplates(arr);
    try { localStorage.setItem(TPL_KEY, JSON.stringify(arr)); } catch (e) { /* ignore */ }
  };

  const saveAsTemplate = () => {
    const name = tplName.trim();
    if (!name) { setTplMsg("Give it a name first."); setTimeout(() => setTplMsg(""), 2500); return; }
    const newTpl = {
      id: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `tpl_${Date.now()}`,
      name,
      niche: nicheKey,
      perShown,
      showFull,
      showSplit,
      rescheduleUrl,
      loom,
      createdAt: new Date().toISOString(),
    };
    persistTemplates([newTpl, ...templates.filter((t) => t.name.toLowerCase() !== name.toLowerCase())]);
    setTplName("");
    setTplPromptOpen(false);
    setTplMsg(`✓ Saved "${name}"`);
    setTimeout(() => setTplMsg(""), 2500);
  };

  const applyTemplate = (tpl) => {
    if (!tpl) return;
    setNicheKey(tpl.niche || "general");
    setPerShown(79);
    setShowFull(tpl.showFull !== false);
    setShowSplit(tpl.showSplit !== false);
    setRescheduleUrl(tpl.rescheduleUrl || "");
    setLoom(tpl.loom || "");
    setTplMsg(`✓ Loaded "${tpl.name}"`);
    setTimeout(() => setTplMsg(""), 2200);
  };

  const deleteTemplate = (id) => {
    persistTemplates(templates.filter((t) => t.id !== id));
  };

  useEffect(() => {
    try { localStorage.setItem("planbuilder.scope", scope); } catch (e) { /* ignore */ }
  }, [scope]);

  // Auto-fill rep name from logged-in user, and load recent plans
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/auth/me`, { credentials: "include" });
        if (res.ok) {
          const me = await res.json();
          if (me?.name) setRepName(me.name);
          if (me?.email) setRepEmail(me.email);
          if (me?.user_id) setMeId(me.user_id);
        }
      } catch (e) { /* not logged in — that's OK */ }
      loadPlans();
    })();
  }, []);

  const loadPlans = async () => {
    setLoadingPlans(true);
    try {
      const res = await fetch(`${API}/api/sales/plans?limit=25`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setPlans(Array.isArray(data) ? data : []);
      }
    } catch (e) { /* ignore */ }
    setLoadingPlans(false);
  };

  const onNicheChange = (k) => {
    setNicheKey(k);
    setPerShown(79);
  };

  // Full URL (with all params) — used for the preview iframe so live edits
  // render instantly without hitting the backend.
  const url = useMemo(() => {
    const params = new URLSearchParams();
    params.set("n", nicheKey);
    if (company.trim()) params.set("co", company.trim());
    if (first.trim()) params.set("first", first.trim());
    const jobNum = parseInt(job, 10);
    const closeNum = parseInt(close, 10);
    const apptsNum = parseInt(appts, 10);
    if (Number.isFinite(jobNum) && jobNum > 0) params.set("job", String(jobNum));
    if (Number.isFinite(closeNum) && closeNum > 0) params.set("close", String(closeNum));
    if (Number.isFinite(apptsNum) && apptsNum > 0) params.set("appts", String(apptsNum));
    params.set("pshown", String(perShown));
    if (exp) params.set("exp", exp);
    if (loom.trim()) params.set("loom", loom.trim());
    if (repName.trim()) params.set("rep", repName.trim());
    if (repPhoto.trim()) params.set("pic", repPhoto.trim());
    if (repEmail.trim()) params.set("repemail", repEmail.trim());
    // Custom-niche label pass-through so /plan renders "For {Name} Companies"
    // even when the slug isn't in the built-in NICHES map.
    if (niche?.custom) {
      params.set("nname", niche.name);
      params.set("nsuffix", niche.suffix);
    }
    // Payment controls — URLs are hardcoded; rep only toggles which buttons show
    if (!showFull) params.set("showfull", "0");
    if (!showSplit) params.set("showsplit", "0");
    if (rescheduleUrl.trim()) params.set("resched", rescheduleUrl.trim());
    if (planId) params.set("pid", planId);
    const base = `${window.location.origin}/plan`;
    return `${base}?${params.toString()}`;
  }, [nicheKey, niche, company, first, job, close, appts, perShown, exp, loom, repName, repPhoto, repEmail, showFull, showSplit, rescheduleUrl, planId]);

  // Short URL — what reps copy and send to clients. Backend resolves `pid`
  // to the saved plan config (rep numbers, pricing, loom, etc.) so the URL
  // stays clean and prospects never see the params.
  const shortUrl = useMemo(
    () => `${window.location.origin}/plan?pid=${encodeURIComponent(planId)}`,
    [planId]
  );

  // Debounce URL → previewSrc so the iframe only reloads after 500ms of no changes
  useEffect(() => {
    const t = setTimeout(() => {
      setPreviewSrc(`${url}${url.includes("?") ? "&" : "?"}preview=1`);
    }, 500);
    return () => clearTimeout(t);
  }, [url]);

  const jobN = parseInt(job, 10) || 0;
  const closeN = parseInt(close, 10) || 0;
  const apptsN = parseInt(appts, 10) || 0;
  const hasAllNumbers = jobN > 0 && closeN > 0 && apptsN > 0;
  const monthlyReturn = hasAllNumbers ? jobN * apptsN * (closeN / 100) : 0;
  const monthlyCost = apptsN > 0 ? apptsN * perShown : 0;

  const doSave = async () => {
    setSaving(true);
    setSaveMsg("");
    try {
      const res = await fetch(`${API}/api/sales/plans`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan_id: planId,
          company,
          first_name: first,
          niche: nicheKey,
          avg_job: jobN || null,
          close_rate: closeN || null,
          appts_per_month: apptsN || null,
          setup_fee: setupFee,
          per_shown: perShown,
          expiry: exp,
          loom_url: loom,
          rep_name: repName,
          rep_photo: repPhoto,
          rep_email: repEmail,
          show_full: showFull,
          show_split: showSplit,
          reschedule_url: rescheduleUrl,
          custom_niche_name: niche?.custom ? niche.name : "",
          custom_niche_suffix: niche?.custom ? niche.suffix : "",
          proposal_url: shortUrl,
        }),
      });
      if (res.ok) {
        setSaveMsg("✓ Saved — tracking is live");
        await loadPlans();
        setSaving(false);
        setTimeout(() => setSaveMsg(""), 3000);
        return true;
      }
      const err = await res.text();
      setSaveMsg("Save failed — " + (err.slice(0, 80) || "please try again"));
    } catch (e) {
      setSaveMsg("Save failed — network error");
    }
    setSaving(false);
    setTimeout(() => setSaveMsg(""), 3000);
    return false;
  };

  const doCopy = async () => {
    // Save-then-copy so the short link always points to fresh server state
    const ok = await doSave();
    if (!ok) return;
    try {
      await navigator.clipboard.writeText(shortUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (e) { /* clipboard denied — no-op */ }
  };

  const startNewPlan = () => {
    // Reset prospect-specific fields and mint a fresh plan_id.
    // Reusable rep/pricing config stays (rep will typically load a template next).
    setCompany("");
    setFirst("");
    setJob("");
    setClose("");
    setAppts("");
    setExp("");
    setPlanId((typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `pid_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);
    setSaveMsg("↻ New plan started");
    setTimeout(() => setSaveMsg(""), 2000);
  };

  return (
    <div data-testid="plan-builder-page" style={{ background: "#060610", minHeight: embedded ? "auto" : "100vh", color: "#fff", fontFamily: "'Manrope', system-ui, sans-serif", padding: embedded ? "24px 16px 60px" : "40px 16px 80px" }}>
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        {/* Top nav — keeps reps anchored to the rest of the software. Hidden when
            embedded inside SalesHub because the parent already has a tab bar. */}
        {!embedded && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 22 }}>
            <a
              href="/"
              data-testid="builder-back-btn"
              style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 16px", borderRadius: 999, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.85)", fontSize: 12.5, fontWeight: 800, textDecoration: "none", fontFamily: "'Manrope', sans-serif" }}
            >
              ← Back to Dashboard
            </a>
            {user && (
              <div style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                <span data-testid="builder-user-email" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10.5, color: "rgba(255,255,255,0.55)", letterSpacing: 0.4 }}>
                  {user.email}
                </span>
                {onLogout && (
                  <button
                    onClick={onLogout}
                    data-testid="builder-logout-btn"
                    style={{ padding: "6px 14px", borderRadius: 999, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.03)", color: "rgba(255,255,255,0.75)", fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: "'Manrope', sans-serif" }}
                  >
                    Log out
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Header */}
        <div style={{ fontSize: 11, letterSpacing: 3, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", fontFamily: "'IBM Plex Mono', monospace", marginBottom: 10, fontWeight: 700 }}>
          Internal · Personal Proposal Builder
        </div>
        <h1 style={{ fontSize: 42, fontWeight: 800, margin: 0, letterSpacing: "-0.02em", fontFamily: "'Manrope', sans-serif" }}>
          Generate a <span style={{ background: "linear-gradient(120deg, #A78BFA, #F472B6)", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent" }}>proposal link</span>
        </h1>
        <p style={{ fontSize: 14, color: "rgba(255,255,255,0.55)", marginTop: 8, marginBottom: 32, lineHeight: 1.5 }}>
          Fill this out after a call and send the link. The proposal pre-fills their numbers automatically.
        </p>

        {/* ═══════ TEMPLATES — one-click load of saved niche+pricing combos ═══════ */}
        <div data-testid="builder-templates" style={{ marginBottom: 24, padding: "14px 16px", border: "1px solid rgba(167,139,250,0.22)", borderRadius: 14, background: "linear-gradient(135deg, rgba(167,139,250,0.05), rgba(244,114,182,0.04))" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: 2, color: "#A78BFA", textTransform: "uppercase", fontWeight: 700 }}>
                My Templates
              </div>
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.4)" }}>
                {templates.length} saved
              </span>
            </div>
            <button
              onClick={() => setTplPromptOpen((v) => !v)}
              data-testid="builder-tpl-toggle"
              style={{
                padding: "6px 14px",
                borderRadius: 999,
                border: "1px solid rgba(167,139,250,0.4)",
                background: tplPromptOpen ? "rgba(167,139,250,0.18)" : "rgba(167,139,250,0.08)",
                color: "#C4B5FD",
                fontSize: 11.5,
                fontWeight: 800,
                cursor: "pointer",
                fontFamily: "'Manrope', sans-serif",
              }}
            >
              {tplPromptOpen ? "× Cancel" : "+ Save current"}
            </button>
          </div>

          {/* Save prompt */}
          {tplPromptOpen && (
            <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
              <input
                type="text"
                value={tplName}
                onChange={(e) => setTplName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveAsTemplate(); }}
                placeholder='Name it — e.g. "Roofing default"'
                data-testid="builder-tpl-name"
                autoFocus
                style={{ flex: "1 1 220px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 10, padding: "10px 14px", color: "#fff", fontSize: 13, fontFamily: "'Manrope', sans-serif", outline: "none" }}
              />
              <button
                onClick={saveAsTemplate}
                data-testid="builder-tpl-save"
                style={{ padding: "10px 18px", borderRadius: 999, border: "none", background: "linear-gradient(135deg, #A78BFA, #F472B6)", color: "#fff", fontSize: 12.5, fontWeight: 800, cursor: "pointer", fontFamily: "'Manrope', sans-serif" }}
              >
                Save template
              </button>
            </div>
          )}

          {/* Template chips */}
          {templates.length === 0 ? (
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", fontStyle: "italic", padding: "6px 2px" }}>
              No templates yet. Set your niche + pricing + loom the way you like, then hit <b style={{ color: "#C4B5FD" }}>+ Save current</b> to reuse it in one click.
            </div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {templates.map((tpl) => (
                <div
                  key={tpl.id}
                  data-testid={`builder-tpl-${tpl.id}`}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 6px 6px 12px", borderRadius: 999, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)", cursor: "pointer", transition: "background .15s, border-color .15s" }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(167,139,250,0.16)"; e.currentTarget.style.borderColor = "rgba(167,139,250,0.5)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.05)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)"; }}
                  onClick={() => applyTemplate(tpl)}
                  title={`${NICHES[tpl.niche]?.name || tpl.niche} · $${tpl.setupFee} setup · $${tpl.perShown}/qualified appt`}
                >
                  <span style={{ fontSize: 12.5, fontWeight: 800, color: "#fff", fontFamily: "'Manrope', sans-serif", letterSpacing: "-0.005em" }}>{tpl.name}</span>
                  <span style={{ fontSize: 10, color: "rgba(255,255,255,0.45)", fontFamily: "'IBM Plex Mono', monospace" }}>
                    · {tpl.niche} · ${tpl.setupFee / 1000}k
                  </span>
                  <span
                    onClick={(e) => { e.stopPropagation(); deleteTemplate(tpl.id); }}
                    data-testid={`builder-tpl-del-${tpl.id}`}
                    role="button"
                    tabIndex={0}
                    aria-label="Delete template"
                    style={{ marginLeft: 4, width: 22, height: 22, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.5)", fontSize: 14, lineHeight: 1, cursor: "pointer", fontFamily: "'Manrope', sans-serif", fontWeight: 700 }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(239,68,68,0.25)"; e.currentTarget.style.color = "#FCA5A5"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; e.currentTarget.style.color = "rgba(255,255,255,0.5)"; }}
                  >
                    ×
                  </span>
                </div>
              ))}
            </div>
          )}

          {tplMsg && (
            <div data-testid="builder-tpl-msg" style={{ marginTop: 10, fontSize: 11.5, color: tplMsg.startsWith("✓") ? "#4ADE80" : "#F87171", fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 0.4 }}>
              {tplMsg}
            </div>
          )}
        </div>

        {/* Form */}
        <div style={{ display: "grid", gap: 16 }}>
          <Field label="Niche">
            <div style={{ display: "flex", gap: 8, alignItems: "stretch", flexWrap: "wrap" }}>
              <select value={nicheKey} onChange={(e) => onNicheChange(e.target.value)} data-testid="builder-niche" style={{ ...inputStyle, flex: "1 1 220px" }}>
                <optgroup label="Built-in niches">
                  {Object.entries(NICHES).map(([k, v]) => (
                    <option key={k} value={k}>{v.name}</option>
                  ))}
                </optgroup>
                {Object.keys(customNiches).length > 0 && (
                  <optgroup label={`Team custom niches (${Object.keys(customNiches).length})`}>
                    {Object.entries(customNiches).map(([k, v]) => (
                      <option key={k} value={k}>{v.name} · team</option>
                    ))}
                  </optgroup>
                )}
              </select>
              <button
                type="button"
                onClick={() => { setShowAddNiche(true); setNicheErr(""); }}
                data-testid="builder-add-niche-btn"
                style={{ padding: "10px 16px", borderRadius: 12, border: "1px dashed rgba(167,139,250,0.5)", background: "rgba(167,139,250,0.08)", color: "#C4B5FD", fontSize: 12.5, fontWeight: 800, cursor: "pointer", fontFamily: "'Manrope', sans-serif", whiteSpace: "nowrap" }}
              >
                + Add niche
              </button>
              {allNiches[nicheKey]?.custom && (
                <button
                  type="button"
                  onClick={() => removeCustomNiche(nicheKey)}
                  data-testid="builder-remove-niche-btn"
                  title="Remove this custom niche"
                  style={{ padding: "10px 14px", borderRadius: 12, border: "1px solid rgba(239,68,68,0.5)", background: "rgba(239,68,68,0.08)", color: "#FCA5A5", fontSize: 12.5, fontWeight: 800, cursor: "pointer", fontFamily: "'Manrope', sans-serif" }}
                >
                  Remove
                </button>
              )}
            </div>
          </Field>

          {/* Add-niche inline form */}
          {showAddNiche && (
            <div data-testid="builder-add-niche-form" style={{ padding: "14px 16px", border: "1px solid rgba(167,139,250,0.35)", borderRadius: 14, background: "linear-gradient(135deg, rgba(167,139,250,0.06), rgba(244,114,182,0.04))" }}>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: 2, color: "#C4B5FD", textTransform: "uppercase", fontWeight: 700, marginBottom: 10 }}>
                Add a custom niche
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
                <Field label="Slug (URL-safe)">
                  <input
                    type="text"
                    value={newNicheSlug}
                    onChange={(e) => { setNewNicheSlug(e.target.value); setNicheErr(""); }}
                    placeholder="e.g. landscaping"
                    style={inputStyle}
                    data-testid="builder-new-niche-slug"
                    autoFocus
                  />
                </Field>
                <Field label="Display name">
                  <input
                    type="text"
                    value={newNicheName}
                    onChange={(e) => { setNewNicheName(e.target.value); setNicheErr(""); }}
                    placeholder="e.g. Landscaping"
                    style={inputStyle}
                    data-testid="builder-new-niche-name"
                  />
                </Field>
              </div>
              <div style={{ marginTop: 10, fontSize: 11, color: "rgba(255,255,255,0.4)", fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 0.4 }}>
                Pricing is flat for every niche: $79 per qualified appt or $250 per shown appt — prospect picks the model.
              </div>
              {nicheErr && (
                <div data-testid="builder-new-niche-err" style={{ marginTop: 8, fontSize: 11.5, color: "#F87171", fontFamily: "'IBM Plex Mono', monospace" }}>
                  {nicheErr}
                </div>
              )}
              <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={saveNewNiche}
                  disabled={nicheBusy}
                  data-testid="builder-new-niche-save"
                  style={{ padding: "10px 20px", borderRadius: 999, border: "none", background: "linear-gradient(135deg, #A78BFA, #F472B6)", color: "#fff", fontSize: 12.5, fontWeight: 800, cursor: nicheBusy ? "wait" : "pointer", fontFamily: "'Manrope', sans-serif", opacity: nicheBusy ? 0.6 : 1 }}
                >
                  {nicheBusy ? "Saving…" : "Save for team"}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowAddNiche(false); setNicheErr(""); }}
                  data-testid="builder-new-niche-cancel"
                  style={{ padding: "10px 16px", borderRadius: 999, border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.03)", color: "rgba(255,255,255,0.75)", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "'Manrope', sans-serif" }}
                >
                  Cancel
                </button>
              </div>
              <div style={{ marginTop: 8, fontSize: 10.5, color: "rgba(255,255,255,0.45)", fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 0.4 }}>
                Team-wide — every rep will see this niche in their dropdown. Pricing is locked at $5,000 setup + $79/qualified appt or $250/shown appt for every niche.
              </div>
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
            <Field label="Company name">
              <input type="text" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Apex Roofing" style={inputStyle} data-testid="builder-company" />
            </Field>
            <Field label="First name">
              <input type="text" value={first} onChange={(e) => setFirst(e.target.value)} placeholder="Mike" style={inputStyle} data-testid="builder-first" />
            </Field>
          </div>

          <div style={{ padding: "14px", border: "1px dashed rgba(255,255,255,0.1)", borderRadius: 14, background: "rgba(255,255,255,0.015)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: 2, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", fontWeight: 700 }}>
                📊 Their numbers (optional)
              </div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", fontStyle: "italic" }}>
                Fill any you know — leave blank to skip the ROI section
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 12 }}>
              <Field label="Avg job value ($)">
                <input type="number" value={job} onChange={(e) => setJob(e.target.value)} placeholder={`e.g. ${niche.defaultJob}`} style={inputStyle} data-testid="builder-job" />
              </Field>
              <Field label="Close rate (%)">
                <input type="number" value={close} onChange={(e) => setClose(e.target.value)} placeholder={`e.g. ${niche.defaultClose}`} style={inputStyle} data-testid="builder-close" />
              </Field>
              <Field label="Appts / month">
                <input type="number" value={appts} onChange={(e) => setAppts(e.target.value)} placeholder="e.g. 10" style={inputStyle} data-testid="builder-appts" />
              </Field>
            </div>
          </div>

          <Field label="Expiry (optional)">
            <input type="date" value={exp} onChange={(e) => setExp(e.target.value)} style={inputStyle} data-testid="builder-exp" />
          </Field>

          {/* Pricing chooser */}
          <div style={{ padding: "16px 14px", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, background: "rgba(255,255,255,0.02)" }}>
            <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: 2, color: "#4ADE80", textTransform: "uppercase", fontWeight: 700, marginBottom: 12 }}>
              💰 Pricing — what the prospect will see
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
              <Field label="Setup fee">
                <div data-testid="builder-setup-locked" style={{ padding: "12px 14px", borderRadius: 12, border: "1px solid rgba(74,222,128,0.35)", background: "rgba(74,222,128,0.08)", color: "#4ADE80", fontWeight: 800, fontSize: 15, fontFamily: "'Manrope', sans-serif", letterSpacing: "-0.01em", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span>{money(setupFee)}</span>
                  <span style={{ fontSize: 10, letterSpacing: 1.6, textTransform: "uppercase", color: "rgba(74,222,128,0.7)", fontFamily: "'IBM Plex Mono', monospace" }}>🔒 Fixed</span>
                </div>
              </Field>

              <Field label="Pricing — locked & flat across every niche">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "10px 12px", borderRadius: 10, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.1)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 18, fontWeight: 800, color: "#4ADE80", fontFamily: "'Manrope', sans-serif" }} data-testid="builder-pshown-fixed">$79</span>
                    <span style={{ fontSize: 10.5, color: "rgba(74,222,128,0.75)", fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 0.4, textTransform: "uppercase" }}>qualified appt</span>
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.8, color: "rgba(255,255,255,0.4)", fontFamily: "'IBM Plex Mono', monospace" }}>OR</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 18, fontWeight: 800, color: "#5AB2FF", fontFamily: "'Manrope', sans-serif" }} data-testid="builder-pshown-alt">$250</span>
                    <span style={{ fontSize: 10.5, color: "rgba(90,178,255,0.75)", fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 0.4, textTransform: "uppercase" }}>shown appt</span>
                  </div>
                  <span style={{ fontSize: 10, letterSpacing: 1.6, textTransform: "uppercase", color: "rgba(255,255,255,0.45)", fontFamily: "'IBM Plex Mono', monospace" }}>🔒</span>
                </div>
                <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.4)", fontFamily: "'IBM Plex Mono', monospace", marginTop: 6, letterSpacing: 0.4 }}>
                  Both options appear on every proposal — the prospect picks their model.
                </div>
              </Field>
            </div>
          </div>

          {/* Payment button toggles — URLs are hardcoded */}
          <div style={{ padding: "16px 14px", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, background: "rgba(255,255,255,0.02)" }}>
            <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: 2, color: "#FBBF24", textTransform: "uppercase", fontWeight: 700, marginBottom: 12 }}>
              💳 Payment buttons — what the prospect can click
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 12 }}>
              <ToggleField
                label="Show full payment"
                checked={showFull}
                onChange={setShowFull}
                testid="builder-showfull"
                color="#F472B6"
              />
              <ToggleField
                label="Show split payment"
                checked={showSplit}
                onChange={setShowSplit}
                testid="builder-showsplit"
                color="#5AB2FF"
              />
            </div>

            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 0.4, lineHeight: 1.5 }}>
              🔒 Checkout URLs are locked to the standard <span style={{ color: "#F472B6" }}>$5,000 full-pay</span> and <span style={{ color: "#5AB2FF" }}>split-in-2</span> flows — no manual URLs to worry about.
            </div>

            <div style={{ marginTop: 12 }}>
              <Field label="Follow-up booking link (optional — Calendly, GHL, etc.)">
                <input
                  type="url"
                  value={rescheduleUrl}
                  onChange={(e) => setRescheduleUrl(e.target.value)}
                  placeholder="https://calendly.com/your-team/follow-up"
                  style={inputStyle}
                  data-testid="builder-resched"
                />
              </Field>
            </div>

            {!showFull && !showSplit && (
              <div style={{ fontSize: 11, color: "#F87171", marginTop: 10, fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 0.4 }}>
                ⚠ Both hidden — the primary button will still show as a fallback so prospects can pay.
              </div>
            )}
          </div>
          {/* Rep attribution */}
          <div style={{ marginTop: 8, padding: "16px 14px", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, background: "rgba(255,255,255,0.02)" }}>
            <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: 2, color: "#A78BFA", textTransform: "uppercase", fontWeight: 700, marginBottom: 12 }}>
              👤 Rep attribution — shows at top of the proposal
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
              <Field label="Your name">
                <input type="text" value={repName} onChange={(e) => setRepName(e.target.value)} placeholder="Jane Doe" style={inputStyle} data-testid="builder-rep-name" />
              </Field>
              <Field label="Your email">
                <input type="email" value={repEmail} onChange={(e) => setRepEmail(e.target.value)} placeholder="jane@skyeai.io" style={inputStyle} data-testid="builder-rep-email" />
              </Field>
              <Field label="Photo URL (optional)">
                <input type="url" value={repPhoto} onChange={(e) => setRepPhoto(e.target.value)} placeholder="https://…/your-photo.jpg" style={inputStyle} data-testid="builder-rep-photo" />
              </Field>
            </div>
          </div>

          {/* Loom */}
          <div style={{ padding: "16px 14px", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, background: "rgba(255,255,255,0.02)" }}>
            <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: 2, color: "#F472B6", textTransform: "uppercase", fontWeight: 700, marginBottom: 12 }}>
              🎥 Loom video (optional) — embeds under the hero
            </div>
            <Field label="Loom share URL">
              <input type="url" value={loom} onChange={(e) => setLoom(e.target.value)} placeholder="https://www.loom.com/share/xxxxxxxx" style={inputStyle} data-testid="builder-loom" />
            </Field>
          </div>
        </div>

        {/* Live summary */}
        <div style={{ marginTop: 24, padding: 16, background: "rgba(74,222,128,0.06)", border: "1px solid rgba(74,222,128,0.25)", borderRadius: 14 }}>
          <div style={{ fontSize: 10, letterSpacing: 2, color: "#4ADE80", textTransform: "uppercase", fontFamily: "'IBM Plex Mono', monospace", marginBottom: 6, fontWeight: 700 }}>
            Sanity Check
          </div>
          <div data-testid="builder-summary" style={{ fontSize: 15, color: "#fff", lineHeight: 1.5 }}>
            <strong>{niche.name}</strong> · {money(setupFee)} setup + $99/qualified appt or $250/shown appt ·{" "}
            {hasAllNumbers ? (
              <>they&apos;d see <span style={{ color: "#4ADE80", fontWeight: 700 }}>{money(monthlyReturn)}</span>/mo return {monthlyCost > 0 && <span style={{ color: "rgba(255,255,255,0.55)" }}>(cost: {money(monthlyCost)}/mo)</span>}.</>
            ) : (
              <span style={{ color: "rgba(255,255,255,0.55)", fontStyle: "italic" }}>no ROI section shown — you didn&apos;t fill in their job value, close rate & appts.</span>
            )}
          </div>
        </div>

        {/* URL preview */}
        <div style={{ marginTop: 24 }}>
          <div style={{ fontSize: 10, letterSpacing: 2, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", fontFamily: "'IBM Plex Mono', monospace", marginBottom: 6, fontWeight: 700 }}>
            Client link — no pricing visible
          </div>
          <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(74,222,128,0.25)", borderRadius: 12, padding: "12px 14px", fontSize: 13, fontFamily: "'IBM Plex Mono', monospace", color: "#4ADE80", wordBreak: "break-all", overflowWrap: "anywhere", lineHeight: 1.5 }} data-testid="builder-url">
            {shortUrl}
          </div>
          <div style={{ marginTop: 6, fontSize: 10.5, color: "rgba(255,255,255,0.4)", fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 0.4 }}>
            Clean short link — clients never see your numbers in the URL. Copy Link saves your changes automatically.
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap", alignItems: "center" }}>
            <button
              onClick={doCopy}
              disabled={saving}
              data-testid="builder-copy"
              style={{
                padding: "12px 22px",
                borderRadius: 999,
                border: "none",
                background: copied ? "#4ADE80" : "linear-gradient(135deg, #A78BFA, #F472B6, #FB923C)",
                color: copied ? "#060610" : "#fff",
                fontSize: 14,
                fontWeight: 800,
                cursor: saving ? "wait" : "pointer",
                fontFamily: "'Manrope', sans-serif",
                transition: "all .2s",
                boxShadow: copied ? "none" : "0 8px 24px -6px rgba(167,139,250,0.4)",
                opacity: saving ? 0.7 : 1,
              }}
            >
              {copied ? "✓ Copied" : saving ? "Saving…" : "💾 Save & Copy link"}
            </button>
            <button
              onClick={doSave}
              disabled={saving}
              data-testid="builder-save"
              style={{
                padding: "12px 22px",
                borderRadius: 999,
                border: "1px solid rgba(74,222,128,0.5)",
                background: "rgba(74,222,128,0.1)",
                color: "#4ADE80",
                fontSize: 14,
                fontWeight: 800,
                cursor: saving ? "wait" : "pointer",
                fontFamily: "'Manrope', sans-serif",
                opacity: saving ? 0.6 : 1,
              }}
            >
              {saving ? "Saving…" : "Save only"}
            </button>
            <button
              onClick={startNewPlan}
              data-testid="builder-new-plan"
              style={{
                padding: "12px 22px",
                borderRadius: 999,
                border: "1px solid rgba(255,255,255,0.15)",
                background: "rgba(255,255,255,0.03)",
                color: "rgba(255,255,255,0.75)",
                fontSize: 14,
                fontWeight: 800,
                cursor: "pointer",
                fontFamily: "'Manrope', sans-serif",
              }}
              title="Clear prospect fields and start a fresh plan with a new tracking id"
            >
              ↻ New plan
            </button>
            <a
              href={`${url}${url.includes("?") ? "&" : "?"}preview=1`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="builder-preview"
              title="Opens the plan in Preview Mode — analytics tracking is paused so your click doesn't count as a view"
              style={{
                padding: "12px 22px",
                borderRadius: 999,
                border: "1px solid rgba(255,255,255,0.15)",
                background: "rgba(255,255,255,0.03)",
                color: "#fff",
                fontSize: 14,
                fontWeight: 800,
                textDecoration: "none",
                fontFamily: "'Manrope', sans-serif",
              }}
            >
              Preview ↗
            </a>
            {saveMsg && (
              <span data-testid="builder-save-msg" style={{ fontSize: 12, color: saveMsg.startsWith("✓") ? "#4ADE80" : "#F87171", fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 0.4 }}>
                {saveMsg}
              </span>
            )}
          </div>
        </div>

        {/* ═══════ LIVE PREVIEW PANEL ═══════ */}
        <div data-testid="builder-live-preview" style={{ marginTop: 34 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: 2, color: "#F472B6", textTransform: "uppercase", fontWeight: 700 }}>
                Live preview
              </div>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 999, background: "rgba(74,222,128,0.12)", border: "1px solid rgba(74,222,128,0.35)", color: "#4ADE80", fontFamily: "'IBM Plex Mono', monospace", fontSize: 9.5, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase" }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#4ADE80", boxShadow: "0 0 8px #4ADE80" }} />
                Real-time
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {/* Device switcher */}
              <div style={{ display: "inline-flex", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 999, padding: 3 }}>
                {[
                  { k: "desktop", label: "Desktop" },
                  { k: "mobile", label: "Mobile" },
                ].map((d) => {
                  const active = previewDevice === d.k;
                  return (
                    <button
                      key={d.k}
                      onClick={() => setPreviewDevice(d.k)}
                      data-testid={`preview-device-${d.k}`}
                      style={{
                        padding: "5px 14px",
                        borderRadius: 999,
                        border: "none",
                        background: active ? "linear-gradient(135deg, #A78BFA, #F472B6)" : "transparent",
                        color: active ? "#fff" : "rgba(255,255,255,0.6)",
                        fontFamily: "'Manrope', sans-serif",
                        fontSize: 11.5,
                        fontWeight: 800,
                        letterSpacing: "-0.005em",
                        cursor: "pointer",
                        transition: "all .2s",
                      }}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
              {/* Collapse toggle */}
              <button
                onClick={() => setPreviewOpen((v) => !v)}
                data-testid="preview-toggle"
                style={{
                  padding: "6px 14px",
                  borderRadius: 999,
                  border: "1px solid rgba(255,255,255,0.15)",
                  background: "rgba(255,255,255,0.03)",
                  color: "#fff",
                  fontSize: 11.5,
                  fontWeight: 800,
                  cursor: "pointer",
                  fontFamily: "'Manrope', sans-serif",
                }}
              >
                {previewOpen ? "Hide ▲" : "Show ▼"}
              </button>
            </div>
          </div>

          {previewOpen && (() => {
            // Device dimensions (real, not scaled)
            const isMobile = previewDevice === "mobile";
            const deviceWidth = isMobile ? 390 : 1280;
            const deviceHeight = isMobile ? 780 : 800;
            // Scale to fit the builder column (max 728px inner content)
            const containerWidth = Math.min(728, typeof window !== "undefined" ? window.innerWidth - 60 : 728);
            const scale = Math.min(1, containerWidth / deviceWidth);
            const scaledHeight = deviceHeight * scale;

            return (
              <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: 14, overflow: "hidden" }}>
                {/* Fake browser chrome */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                  <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#EF4444" }} />
                  <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#F59E0B" }} />
                  <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#22C55E" }} />
                  <div style={{ flex: 1, marginLeft: 6, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 999, padding: "4px 12px", fontSize: 10.5, color: "rgba(255,255,255,0.55)", fontFamily: "'IBM Plex Mono', monospace", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {previewSrc || "loading…"}
                  </div>
                  <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.4)", letterSpacing: 0.6 }}>
                    {deviceWidth}×{deviceHeight}
                  </span>
                </div>

                {/* Scaled iframe wrapper */}
                <div
                  style={{
                    width: "100%",
                    height: scaledHeight,
                    display: "flex",
                    justifyContent: "center",
                    background: "#060610",
                    borderRadius: 12,
                    border: "1px solid rgba(255,255,255,0.06)",
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  {previewSrc ? (
                    <iframe
                      key={previewSrc}
                      src={previewSrc}
                      data-testid="preview-iframe"
                      title="Plan preview"
                      style={{
                        width: deviceWidth,
                        height: deviceHeight,
                        border: "none",
                        transform: `scale(${scale})`,
                        transformOrigin: "top center",
                        background: "#060610",
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <div style={{ alignSelf: "center", color: "rgba(255,255,255,0.4)", fontSize: 12, fontFamily: "'IBM Plex Mono', monospace" }}>
                      Loading preview…
                    </div>
                  )}
                </div>

                <div style={{ marginTop: 10, fontSize: 11, color: "rgba(255,255,255,0.45)", fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 0.4, textAlign: "center" }}>
                  Updates 0.5s after you stop typing · Preview Mode (no analytics)
                </div>
              </div>
            );
          })()}
        </div>

        {/* Recent plans */}
        <div style={{ marginTop: 44 }}>
          {(() => {
            const mineCount = plans.filter((p) => meId && p.user_id === meId).length;
            const teamCount = plans.length;
            const visiblePlans = scope === "mine"
              ? plans.filter((p) => meId && p.user_id === meId)
              : plans;
            return (
              <>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
                  <div>
                    <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: 2, color: "#5AB2FF", textTransform: "uppercase", fontWeight: 700, marginBottom: 4 }}>
                      Team Hub · Recent Plans
                    </div>
                    <h2 style={{ fontSize: 22, fontWeight: 800, margin: 0, letterSpacing: "-0.01em", fontFamily: "'Manrope', sans-serif" }}>
                      {scope === "mine" ? "What you've sent" : "What your team has sent"}
                    </h2>
                  </div>
                  <button
                    onClick={loadPlans}
                    disabled={loadingPlans}
                    data-testid="builder-refresh-plans"
                    style={{ padding: "8px 14px", borderRadius: 999, border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.03)", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Manrope', sans-serif", opacity: loadingPlans ? 0.5 : 1 }}
                  >
                    {loadingPlans ? "…" : "↻ Refresh"}
                  </button>
                </div>

                {/* Scope toggle — Just mine / Whole team */}
                <div
                  data-testid="scope-toggle"
                  style={{
                    display: "inline-flex",
                    padding: 4,
                    borderRadius: 999,
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    marginBottom: 14,
                  }}
                >
                  {[
                    { key: "mine", label: "Just mine", count: mineCount, color: "#4ADE80" },
                    { key: "team", label: "Whole team", count: teamCount, color: "#5AB2FF" },
                  ].map((opt) => {
                    const active = scope === opt.key;
                    return (
                      <button
                        key={opt.key}
                        onClick={() => setScope(opt.key)}
                        data-testid={`scope-${opt.key}`}
                        style={{
                          padding: "8px 16px",
                          borderRadius: 999,
                          border: "none",
                          background: active ? `linear-gradient(135deg, ${opt.color}33, ${opt.color}18)` : "transparent",
                          boxShadow: active ? `inset 0 0 0 1px ${opt.color}66` : "none",
                          color: active ? "#fff" : "rgba(255,255,255,0.55)",
                          fontSize: 13,
                          fontWeight: 800,
                          cursor: "pointer",
                          fontFamily: "'Manrope', sans-serif",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          transition: "all .15s",
                        }}
                      >
                        {opt.label}
                        <span
                          style={{
                            fontSize: 10,
                            padding: "1px 7px",
                            borderRadius: 999,
                            background: active ? `${opt.color}33` : "rgba(255,255,255,0.06)",
                            color: active ? opt.color : "rgba(255,255,255,0.55)",
                            fontFamily: "'IBM Plex Mono', monospace",
                            fontWeight: 700,
                          }}
                        >
                          {opt.count}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {visiblePlans.length === 0 && !loadingPlans && (
                  <div data-testid="builder-plans-empty" style={{ background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(255,255,255,0.12)", borderRadius: 14, padding: "24px 18px", textAlign: "center", fontSize: 13, color: "rgba(255,255,255,0.55)" }}>
                    {scope === "mine" && plans.length > 0 ? (
                      <>You haven&apos;t sent any plans yet — switch to <b style={{ color: "#5AB2FF" }}>Whole team</b> to see what your teammates are sending.</>
                    ) : (
                      <>No saved plans yet. Fill in the form above and hit <b style={{ color: "#4ADE80" }}>Save to team hub</b> to keep a record of every proposal your team sends.</>
                    )}
                  </div>
                )}

                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {visiblePlans.map((p, i) => (
                    <PlanRow key={p.plan_id || i} plan={p} idx={i} onDeleted={loadPlans} apiBase={API} meId={meId} />
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%",
  minWidth: 0,
  padding: "12px 14px",
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,0.1)",
  background: "rgba(255,255,255,0.03)",
  color: "#fff",
  fontSize: 15,
  fontFamily: "'Manrope', sans-serif",
  outline: "none",
  boxSizing: "border-box",
};

function Field({ label, children }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: 10, letterSpacing: 2, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", fontFamily: "'IBM Plex Mono', monospace", marginBottom: 6, fontWeight: 700 }}>
        {label}
      </div>
      {children}
    </label>
  );
}

function ToggleField({ label, checked, onChange, testid, color = "#4ADE80" }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      data-testid={testid}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 14px",
        borderRadius: 12,
        border: checked ? `1px solid ${color}` : "1px solid rgba(255,255,255,0.1)",
        background: checked ? `${color}18` : "rgba(255,255,255,0.03)",
        cursor: "pointer",
        fontFamily: "'Manrope', sans-serif",
        fontSize: 13,
        fontWeight: 700,
        color: checked ? "#fff" : "rgba(255,255,255,0.6)",
        textAlign: "left",
        transition: "all .15s",
      }}
    >
      <span
        aria-hidden
        style={{
          width: 34,
          height: 20,
          borderRadius: 999,
          background: checked ? color : "rgba(255,255,255,0.15)",
          position: "relative",
          transition: "background .15s",
          flexShrink: 0,
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 2,
            left: checked ? 16 : 2,
            width: 16,
            height: 16,
            borderRadius: "50%",
            background: "#fff",
            transition: "left .18s",
            boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
          }}
        />
      </span>
      <span>{label}</span>
    </button>
  );
}

// Format seconds as "5m 20s", "1h 12m", or "45s"
function formatDuration(secs) {
  const s = Math.max(0, Math.round(Number(secs) || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return rem ? `${m}m ${rem}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm ? `${h}h ${rm}m` : `${h}h`;
}

// Sections in the display order they appear on /plan
const SECTION_LABELS = {
  rep: "Rep intro",
  hero: "Hero",
  how: "How it works",
  loom: "Loom video",
  pillars: "What you get",
  team: "Team",
  numbers: "Your numbers",
  pricing: "Pricing",
  testimonials: "Testimonials",
  cta: "CTA",
};

function PlanRow({ plan, idx, onDeleted, apiBase, meId }) {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const [deleteState, setDeleteState] = useState("idle"); // idle | confirm | deleting
  const label = (plan.company || plan.first_name || "Untitled prospect").trim() || "Untitled prospect";
  const niche = NICHES[plan.niche] || NICHES.general;
  const isMine = meId && plan.user_id === meId;
  const a = plan.analytics || { views_count: 0, total_seconds: 0, last_viewed_at: null, cta_clicks: 0, demo_calls: 0, sections: {} };
  const hasViews = (a.views_count || 0) > 0;
  const doCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(plan.proposal_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch (err) { /* ignore */ }
  };
  const doDelete = async (e) => {
    e.stopPropagation();
    if (deleteState === "idle") {
      setDeleteState("confirm");
      setTimeout(() => setDeleteState((s) => (s === "confirm" ? "idle" : s)), 3000);
      return;
    }
    if (deleteState !== "confirm") return;
    setDeleteState("deleting");
    try {
      const res = await fetch(`${apiBase}/api/sales/plans/${encodeURIComponent(plan.plan_id)}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok && typeof onDeleted === "function") await onDeleted();
      else setDeleteState("idle");
    } catch (err) {
      setDeleteState("idle");
    }
  };

  const secEntries = Object.entries(a.sections || {}).sort((x, y) => (y[1] || 0) - (x[1] || 0));
  const maxSec = secEntries.length ? Math.max(...secEntries.map(([, v]) => v || 0)) : 0;

  return (
    <div data-testid={`plan-row-${idx}`} style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, overflow: "hidden", transition: "border-color .2s" }}>
      {/* Row header (click to expand) */}
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`plan-row-toggle-${idx}`}
        style={{ width: "100%", background: "transparent", border: "none", color: "inherit", cursor: "pointer", padding: "14px 16px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", textAlign: "left", fontFamily: "inherit" }}
      >
        <div style={{ flex: "1 1 240px", minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>{label}</div>
            {isMine && (
              <span style={{ background: "rgba(74,222,128,0.12)", border: "1px solid rgba(74,222,128,0.35)", color: "#4ADE80", fontSize: 9, fontWeight: 700, padding: "1px 7px", borderRadius: 999, fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 0.6, textTransform: "uppercase" }}>
                You
              </span>
            )}
            {hasViews && (
              <span data-testid={`plan-row-views-badge-${idx}`} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "rgba(74,222,128,0.15)", border: "1px solid rgba(74,222,128,0.4)", color: "#4ADE80", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 999, fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 0.4 }}>
                👁 {a.views_count}
              </span>
            )}
            {(a.cta_clicks || 0) > 0 && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "rgba(251,191,36,0.15)", border: "1px solid rgba(251,191,36,0.4)", color: "#FBBF24", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 999, fontFamily: "'IBM Plex Mono', monospace" }}>
                🔥 {a.cta_clicks} CTA
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 0.4, marginTop: 2 }}>
            {niche.name} · by {plan.user_name || plan.rep_name || "—"} · sent {formatWhen(plan.created_at)}
          </div>
          {hasViews ? (
            <div data-testid={`plan-row-analytics-${idx}`} style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", marginTop: 6, fontFamily: "'IBM Plex Mono', monospace" }}>
              Last viewed <b style={{ color: "#5AB2FF" }}>{formatWhen(a.last_viewed_at)}</b> · total time <b style={{ color: "#4ADE80" }}>{formatDuration(a.total_seconds)}</b>
            </div>
          ) : (
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginTop: 6, fontFamily: "'IBM Plex Mono', monospace", fontStyle: "italic" }}>
              Not opened yet
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span
            onClick={doCopy}
            data-testid={`plan-row-copy-${idx}`}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") doCopy(e); }}
            style={{ padding: "8px 14px", borderRadius: 999, border: "1px solid rgba(74,222,128,0.4)", background: copied ? "#4ADE80" : "rgba(74,222,128,0.08)", color: copied ? "#060610" : "#4ADE80", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "'Manrope', sans-serif", display: "inline-block" }}
          >
            {copied ? "✓" : "📋 Copy"}
          </span>
          <a
            href={`${plan.proposal_url}${plan.proposal_url.includes("?") ? "&" : "?"}preview=1`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            data-testid={`plan-row-open-${idx}`}
            title="Opens in Preview Mode — analytics paused"
            style={{ padding: "8px 14px", borderRadius: 999, border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.03)", color: "#fff", fontSize: 12, fontWeight: 800, textDecoration: "none", fontFamily: "'Manrope', sans-serif" }}
          >
            Open ↗
          </a>
          <span
            onClick={doDelete}
            data-testid={`plan-row-delete-${idx}`}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") doDelete(e); }}
            title={deleteState === "confirm" ? "Click again to confirm deletion" : "Delete this plan"}
            style={{
              padding: "8px 12px",
              borderRadius: 999,
              border: deleteState === "confirm" ? "1px solid rgba(248,113,113,0.7)" : "1px solid rgba(255,255,255,0.12)",
              background: deleteState === "confirm" ? "rgba(248,113,113,0.18)" : "rgba(255,255,255,0.03)",
              color: deleteState === "confirm" ? "#F87171" : "rgba(255,255,255,0.55)",
              fontSize: 13,
              fontWeight: 800,
              cursor: deleteState === "deleting" ? "wait" : "pointer",
              fontFamily: "'Manrope', sans-serif",
              display: "inline-block",
              transition: "all .15s",
              opacity: deleteState === "deleting" ? 0.5 : 1,
            }}
          >
            {deleteState === "confirm" ? "Confirm?" : deleteState === "deleting" ? "…" : "🗑"}
          </span>
          <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 14, transition: "transform .2s", transform: open ? "rotate(90deg)" : "rotate(0deg)" }}>▸</span>
        </div>
      </button>

      {/* Expanded analytics panel */}
      {open && (
        <div data-testid={`plan-row-analytics-panel-${idx}`} style={{ background: "rgba(255,255,255,0.02)", borderTop: "1px solid rgba(255,255,255,0.08)", padding: "18px 20px" }}>
          {!hasViews ? (
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", textAlign: "center", padding: "10px 0" }}>
              Nothing yet. When your prospect opens this link, you&apos;ll see how long they spent on each section here.
            </div>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 8, marginBottom: 18 }}>
                <StatChip label="Opens" value={a.views_count} color="#5AB2FF" />
                <StatChip label="Total time" value={formatDuration(a.total_seconds)} color="#4ADE80" />
                <StatChip label="Avg / view" value={formatDuration(a.total_seconds / Math.max(1, a.views_count))} color="#A78BFA" />
                <StatChip label="CTA clicks" value={a.cta_clicks || 0} color="#FBBF24" />
                <StatChip label="Demo calls" value={a.demo_calls || 0} color="#4ADE80" />
              </div>
              <div style={{ fontSize: 10, letterSpacing: 2, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", fontFamily: "'IBM Plex Mono', monospace", marginBottom: 10, fontWeight: 700 }}>
                Time per section
              </div>
              {secEntries.length === 0 ? (
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", fontStyle: "italic" }}>No section data yet.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {secEntries.map(([sec, secs]) => (
                    <div key={sec} style={{ display: "grid", gridTemplateColumns: "120px 1fr 60px", gap: 10, alignItems: "center" }}>
                      <span style={{ fontSize: 12, color: "rgba(255,255,255,0.75)", fontFamily: "'Manrope', sans-serif", fontWeight: 600 }}>
                        {SECTION_LABELS[sec] || sec}
                      </span>
                      <div style={{ height: 8, background: "rgba(255,255,255,0.05)", borderRadius: 999, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${maxSec > 0 ? (secs / maxSec) * 100 : 0}%`, background: "linear-gradient(90deg, #5AB2FF, #4ADE80)", borderRadius: 999, transition: "width .3s" }} />
                      </div>
                      <span style={{ fontSize: 11, color: "#4ADE80", fontFamily: "'IBM Plex Mono', monospace", textAlign: "right", fontWeight: 700 }}>
                        {formatDuration(secs)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function StatChip({ label, value, color }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "10px 12px", textAlign: "center" }}>
      <div style={{ fontSize: 18, fontWeight: 800, color, fontFamily: "'Manrope', sans-serif", letterSpacing: "-0.01em" }}>{value}</div>
      <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: 1.4, color: "rgba(255,255,255,0.45)", marginTop: 2, fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700 }}>{label}</div>
    </div>
  );
}
